import { styled } from "@mui/material";
import * as React from "react";
import Localization from "../core/Localization";
import Sys from "../core/Sys";
import Button from "../coreui/Button";
import Icon from "../coreui/Icon";
import Menu from "../coreui/Menu";
import MenuItem from "../coreui/MenuItem";
import { TableChildProps } from "../coreui/Table";
import { PaginationContext } from "../coreui/table/PaginationContext";
import Typography from "../coreui/Typography";

const GroupContainer = styled("div")(({ theme }) => ({
  "&:focus-visible": {
    outline: "2px solid",
    outlineColor: theme.palette.grey[800],
    outlineOffset: "8px",
  },
  alignItems: "center",
  display: "flex",
}));

const MenuButton = styled(Button)({
  fontSize: 12,
  fontWeight: 400,
  letterSpacing: "normal",
  marginLeft: 8,
  marginRight: 12,
  minWidth: 0,
  paddingLeft: 4,
  paddingRight: 4,
  textTransform: "none",
});

const GridPager: React.FC<{ propagated: TableChildProps }> = (props) => {
  const pagination = React.useContext(PaginationContext);
  const [menuButtonElement, setMenuButtonElement] =
    React.useState<HTMLButtonElement | null>(null);
  const componentId = React.useRef<string>(`grid-pager-${Sys.nextId}`);
  const isMounted = React.useRef<boolean>(false);
  const nextButtonRef = React.useRef<HTMLButtonElement>(null);
  const paginationChangedByUser = React.useRef<boolean>(false);
  const prevButtonRef = React.useRef<HTMLButtonElement>(null);

  const accessibleDescriptionId = `${componentId.current}-accessible-description`;
  const rowsPerPageId = `${componentId.current}-rows-per-page`;

  React.useEffect(() => {
    isMounted.current = true;
    pagination.initialize();
    return () => {
      isMounted.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (!paginationChangedByUser.current) {
      // Pagination was not changed by the user
      return;
    }

    paginationChangedByUser.current = false;

    if (pagination.isAtFirstPage && nextButtonRef.current) {
      // The prev button will be disabled, so ensure focus is not lost
      nextButtonRef.current.focus();
    } else if (pagination.isAtLastPage && prevButtonRef.current) {
      // The next button will be disabled, so ensure focus is not lost
      prevButtonRef.current.focus();
    }

    if (!(pagination.isAtFirstPage && pagination.isAtLastPage)) {
      // Only announce if there is more than one page
      Sys.debounceMethod(
        () => {
          if (isMounted.current) {
            Sys.announce(
              Localization.getBuiltInMessage("DataTable.paginationAlert", {
                currentPageFirstRow: pagination.firstRow.toString(),
                currentPageLastRow: pagination.lastRow.toString(),
                totalRows: pagination.totalRows.toString(),
              })
            );
          }
        },
        "DataTable.paginationAlert",
        1000
      );
    }
  }, [pagination.firstRow, pagination.lastRow, pagination.totalRows]);

  const getPaginationMessage = (): string => {
    if (pagination.pageSize === 1) {
      const translationArgs = {
        currentRow: pagination.firstRow.toString(),
        totalRows: pagination.totalRows.toString(),
      };

      return Localization.getBuiltInMessage("rowNumber", translationArgs);
    } else {
      const translationArgs = {
        currentPageFirstRow: pagination.firstRow.toString(),
        currentPageLastRow: pagination.lastRow.toString(),
        totalRows: pagination.totalRows.toString(),
      };

      return Localization.getBuiltInMessage("visibleRows", translationArgs);
    }
  };

  const getAccessibleDescription = (): string => {
    const translationArgs = {
      currentPageFirstRow: pagination.firstRow.toString(),
      currentPageLastRow: pagination.lastRow.toString(),
      totalRows: pagination.totalRows.toString(),
    };

    return Localization.getBuiltInMessage(
      "DataTable.paginationAlert",
      translationArgs
    );
  };

  const isFullWidth = props.propagated.parentTable.isVerticalLayout;

  const prevButton = (
    <Button
      aria-label={Localization.getBuiltInMessage("previous")}
      disabled={pagination.isAtFirstPage}
      icon="fas fa-chevron-left"
      onClick={() => {
        pagination.goToPreviousPage();
        paginationChangedByUser.current = true;
      }}
      ref={prevButtonRef}
      size="small"
      style={{
        marginLeft: isFullWidth ? 0 : 24,
        marginRight: isFullWidth ? 24 : 8,
      }}
      tabIndex={-1}
    />
  );

  // Describedby moved from outer div to button so both nvda and
  // voiceover will announce it.
  return (
    <div
      style={{
        alignItems: "center",
        display: "flex",
      }}
    >
      {isFullWidth ? prevButton : null}
      <GroupContainer
        aria-label={Localization.getBuiltInMessage(
          "DataTable.pagerDescription",
          { description: props.propagated.parentTable.description }
        )}
        role="group"
        style={{
          flex: isFullWidth ? "auto" : undefined,
          justifyContent: isFullWidth ? "center" : undefined,
        }}
      >
        <Typography aria-hidden={true} id={rowsPerPageId} variant="caption">
          {Localization.getBuiltInMessage("rows")}
        </Typography>
        <MenuButton
          aria-describedby={accessibleDescriptionId}
          aria-expanded={menuButtonElement !== null}
          aria-haspopup="listbox"
          aria-labelledby={`${rowsPerPageId} ${componentId}`}
          id={componentId.current}
          onClick={(event: React.MouseEvent<HTMLButtonElement>): void => {
            const target: HTMLButtonElement = event.currentTarget;
            setMenuButtonElement((prev) => (prev ? null : target));
          }}
          size="small"
          tabIndex={-1}
          style={{ minWidth: 50 }}
          variant="text"
        >
          {pagination.pageSize === null ? "All" : pagination.pageSize}
          <Icon
            icon="fas fa-caret-down"
            style={{ fontSize: 16, marginLeft: ".4em" }}
          />
        </MenuButton>
        <Menu
          anchorEl={menuButtonElement}
          labelId={rowsPerPageId}
          modal={true}
          onClose={() => setMenuButtonElement(null)}
          variant="selectedMenu"
        >
          {pagination.pageSizes.map((p) => (
            <MenuItem
              aria-selected={p === pagination.pageSize}
              key={p}
              onClick={() => {
                pagination.setPageSize(p);
                setMenuButtonElement(null);
              }}
              role="option"
              selected={p === pagination.pageSize}
            >
              {p === null ? "All" : p.toString()}
            </MenuItem>
          ))}
        </Menu>
        <Typography
          aria-hidden={true}
          style={{ lineHeight: "26px", whiteSpace: "nowrap" }}
          variant="caption"
        >
          {getPaginationMessage()}
        </Typography>
      </GroupContainer>
      {!isFullWidth ? prevButton : null}
      <Button
        aria-label={Localization.getBuiltInMessage("next")}
        disabled={pagination.isAtLastPage}
        icon="fas fa-chevron-right"
        onClick={() => {
          pagination.goToNextPage();
          paginationChangedByUser.current = true;
        }}
        ref={nextButtonRef}
        size="small"
        style={{ marginLeft: isFullWidth ? 24 : 0 }}
        tabIndex={-1}
      />
      <div id={accessibleDescriptionId} style={{ display: "none" }}>
        {getAccessibleDescription()}
      </div>
    </div>
  );
};

export default GridPager;
